import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Pagination, Alert } from 'react-bootstrap';
import { apiService } from '../services/api';
import type { OrganizationMicrositeResponse } from '../services/services/organizationService';
import type { Event } from '../types';
import EventCard from '../components/EventCard';
import SEO from '../components/common/SEO';
import { useAuth } from '../contexts/AuthContext';
import DjangoFallback404 from '../components/ErrorPages/DjangoFallback404';
import './OrganizationMicrositePage.scss';

const OrganizationMicrositePage: React.FC = () => {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [data, setData] = useState<OrganizationMicrositeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [subscribeMessage, setSubscribeMessage] = useState<string | null>(null);
  const [shouldCheckDjango, setShouldCheckDjango] = useState(false);

  const page = parseInt(searchParams.get('page') || '1', 10);
  const search = searchParams.get('search') || '';
  const status = searchParams.get('status') || '';
  const ordering = searchParams.get('ordering') || '-starts';

  const fetchMicrosite = useCallback(async () => {
    if (!orgSlug) return;
    try {
      setLoading(true);
      setShouldCheckDjango(false);
      setError(null);
      const response = await apiService.organization.getMicrosite(orgSlug, {
        page,
        search: search || undefined,
        status: status || undefined,
        ordering,
      });
      setData(response);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setShouldCheckDjango(true);
        setError(null);
        setData(null);
        return;
      }
      setError('Failed to load organization page');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [orgSlug, page, search, status, ordering]);

  useEffect(() => {
    fetchMicrosite();
  }, [fetchMicrosite]);

  const updateFilters = (updates: { page?: number; search?: string; status?: string; ordering?: string }) => {
    const next = new URLSearchParams(searchParams);
    if (updates.page !== undefined) next.set('page', updates.page.toString());
    if (updates.search !== undefined) next.set('search', updates.search);
    if (updates.status !== undefined) next.set('status', updates.status);
    if (updates.ordering !== undefined) next.set('ordering', updates.ordering);
    setSearchParams(next);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const input = form.querySelector<HTMLInputElement>('input[name="search"]');
    updateFilters({ search: input?.value || '', page: 1 });
  };

  const handleSubscribe = async () => {
    if (!orgSlug || !isAuthenticated) {
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/accounts/login/?next=${next}`;
      return;
    }
    try {
      setSubscribeLoading(true);
      setSubscribeMessage(null);
      const result = await apiService.organization.subscribe(orgSlug);
      setSubscribeMessage(result.message);
      if (result.success && !result.already_subscribed) {
        fetchMicrosite();
      }
    } catch (err) {
      setSubscribeMessage('Failed to subscribe');
    } finally {
      setSubscribeLoading(false);
    }
  };

  if (!orgSlug) {
    return (
      <Container className="py-5">
        <Alert variant="warning">Missing organization slug.</Alert>
      </Container>
    );
  }

  if (shouldCheckDjango) {
    return <DjangoFallback404 />;
  }

  if (loading && !data) {
    return (
      <Container className="py-5">
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container className="py-5">
        <SEO title="Organization" />
        <Alert variant="danger">{error || 'Organization not found.'}</Alert>
      </Container>
    );
  }

  const org = data.organization;
  const socialLinks = org.social_links || {};
  const hasSocial = Object.values(socialLinks).some(Boolean);
  const hasFacebook = !!socialLinks.facebook;
  const hasInstagram = !!socialLinks.instagram;

  return (
    <>
      <SEO
        title={org.meta_title}
        description={org.meta_description}
        image={org.custom_banner_url || org.logo_url || undefined}
      />
      {org.custom_css && (
        <style
          dangerouslySetInnerHTML={{
            __html: `:root { --org-primary-color: ${org.primary_color}; --org-secondary-color: ${org.secondary_color}; } ${org.custom_css}`,
          }}
        />
      )}

      <div className="organization-microsite-page">
        {/* Hero */}
        <div className="org-hero">
          {org.custom_banner_url && (
            <div className="org-hero-banner" style={{ backgroundImage: `url('${org.custom_banner_url}')` }} />
          )}
          <div className="org-hero-overlay" />
          <Container className="org-hero-content">
            <Row className="align-items-center">
              <Col md="auto" className="mb-3 mb-md-0">
                {org.logo_url ? (
                  <img src={org.logo_url} alt={org.name} className="org-hero-logo" />
                ) : (
                  <div className="org-hero-logo-placeholder">
                    <span>{org.name.slice(0, 2).toUpperCase()}</span>
                  </div>
                )}
              </Col>
              <Col>
                <h1 className="org-hero-title">{org.hero_title}</h1>
                {org.hero_subtitle && <p className="org-hero-subtitle">{org.hero_subtitle}</p>}
                {hasSocial && (
                  <div className="org-social-links mt-2">
                    {socialLinks.website && (
                      <a href={socialLinks.website} target="_blank" rel="noopener noreferrer" className="btn btn-org-social">
                        <i className="fas fa-globe" /> Website
                      </a>
                    )}
                    {socialLinks.facebook && (
                      <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="btn btn-org-social">
                        <i className="fab fa-facebook-f" /> Facebook
                      </a>
                    )}
                    {socialLinks.instagram && (
                      <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="btn btn-org-social">
                        <i className="fab fa-instagram" /> Instagram
                      </a>
                    )}
                    {socialLinks.twitter && (
                      <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="btn btn-org-social">
                        <i className="fab fa-twitter" /> Twitter
                      </a>
                    )}
                    {socialLinks.linkedin && (
                      <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="btn btn-org-social">
                        <i className="fab fa-linkedin-in" /> LinkedIn
                      </a>
                    )}
                    {socialLinks.youtube && (
                      <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="btn btn-org-social">
                        <i className="fab fa-youtube" /> YouTube
                      </a>
                    )}
                    {socialLinks.tiktok && (
                      <a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer" className="btn btn-org-social">
                        <i className="fab fa-tiktok" /> TikTok
                      </a>
                    )}
                  </div>
                )}
                {org.enable_newsletter_signup && (
                  <div className="org-subscribe-section mt-3">
                    {subscribeMessage && <span className="me-2 text-white small">{subscribeMessage}</span>}
                    {isAuthenticated ? (
                      org.is_subscribed ? (
                        <Button variant="outline-light" disabled>
                          <i className="fas fa-check-circle me-2" /> Subscribed
                        </Button>
                      ) : (
                        <Button variant="outline-light" onClick={handleSubscribe} disabled={subscribeLoading}>
                          {subscribeLoading ? '...' : <><i className="fas fa-bell me-2" /> Subscribe for Updates</>}
                        </Button>
                      )
                    ) : (
                      <a
                        href={`/accounts/login/?next=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                        className="btn btn-outline-light"
                      >
                        <i className="fas fa-sign-in-alt me-2" /> Login to Subscribe
                      </a>
                    )}
                  </div>
                )}
              </Col>
            </Row>
          </Container>
        </div>

        <Container className="py-5">
          {/* Search and filters */}
          <div className="org-filters mb-4">
            <Row className="g-3">
              <Col md={6}>
                <Form onSubmit={handleSearchSubmit} className="d-flex gap-2">
                  <Form.Control
                    type="text"
                    name="search"
                    placeholder="Search events..."
                    defaultValue={search}
                    className="org-search-input"
                  />
                  <Button type="submit" variant="primary">
                    <i className="fas fa-search me-2" /> Search
                  </Button>
                </Form>
              </Col>
              <Col md={6}>
                <div className="d-flex gap-2 justify-content-md-end flex-wrap">
                  <Form.Select
                    value={status}
                    onChange={(e) => updateFilters({ status: e.target.value, page: 1 })}
                    className="org-filter-select"
                    style={{ width: 'auto' }}
                  >
                    <option value="">All Events</option>
                    <option value="scheduled">Upcoming</option>
                    <option value="ongoing">Ongoing</option>
                    <option value="finished">Past</option>
                  </Form.Select>
                  <Form.Select
                    value={ordering}
                    onChange={(e) => updateFilters({ ordering: e.target.value, page: 1 })}
                    className="org-filter-select"
                    style={{ width: 'auto' }}
                  >
                    <option value="-starts">Latest First</option>
                    <option value="starts">Earliest First</option>
                  </Form.Select>
                </div>
              </Col>
            </Row>
          </div>

          <Row>
            <Col lg={hasFacebook || hasInstagram ? 8 : 12}>
              {/* Upcoming events */}
              {data.upcoming_events && data.upcoming_events.length > 0 && (
                <section className="mb-5">
                  <h2 className="org-section-title">Upcoming Events</h2>
                  <Row className="row-cols-1 row-cols-md-3 g-4">
                    {(data.upcoming_events as Event[]).map((event) => (
                      <Col key={event.id}>
                        <EventCard event={event} />
                      </Col>
                    ))}
                  </Row>
                </section>
              )}

              {/* Main events list (paginated) */}
              <section className="mb-5">
                {data.events.length === 0 && (!data.upcoming_events || data.upcoming_events.length === 0) ? (
                  <div className="org-no-events">
                    <h3>No events found</h3>
                    <p>Check back later for upcoming events from {org.name}</p>
                  </div>
                ) : (
                  <>
                    <h2 className="org-section-title">Events</h2>
                    <Row className="row-cols-1 row-cols-md-3 g-4">
                      {(data.events as Event[]).map((event) => (
                        <Col key={event.id}>
                          <EventCard event={event} />
                        </Col>
                      ))}
                    </Row>
                    {data.pagination && data.pagination.total_pages > 1 && (
                      <div className="d-flex justify-content-center mt-4">
                        <Pagination>
                          <Pagination.Prev
                            disabled={!data.pagination.has_previous}
                            onClick={() => updateFilters({ page: data.pagination.previous_page! })}
                          />
                          <Pagination.Item disabled>
                            {data.pagination.current_page} / {data.pagination.total_pages}
                          </Pagination.Item>
                          <Pagination.Next
                            disabled={!data.pagination.has_next}
                            onClick={() => updateFilters({ page: data.pagination.next_page! })}
                          />
                        </Pagination>
                      </div>
                    )}
                  </>
                )}
              </section>

              {/* Past events */}
              {org.show_past_events && data.past_events && data.past_events.length > 0 && (
                <section className="mb-5">
                  <h2 className="org-section-title">Past Events</h2>
                  <Row className="row-cols-1 row-cols-md-3 g-4">
                    {(data.past_events as Event[]).map((event) => (
                      <Col key={event.id}>
                        <EventCard event={event} />
                      </Col>
                    ))}
                  </Row>
                </section>
              )}
            </Col>

            {(hasFacebook || hasInstagram) && (
              <Col lg={4}>
                <div className="org-social-feed sticky-top">
                  <h2 className="org-section-title">
                    <i className="fas fa-share-alt me-2" /> Social Media
                  </h2>
                  {hasFacebook && (
                    <div className="org-embed mb-3">
                      <a href={socialLinks.facebook!} target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary btn-sm">
                        <i className="fab fa-facebook me-2" /> View on Facebook
                      </a>
                    </div>
                  )}
                  {hasInstagram && (
                    <div className="org-embed">
                      <a href={socialLinks.instagram!} target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary btn-sm">
                        <i className="fab fa-instagram me-2" /> View on Instagram
                      </a>
                    </div>
                  )}
                </div>
              </Col>
            )}
          </Row>
        </Container>
      </div>
    </>
  );
};

export default OrganizationMicrositePage;
